import { useCallback, useEffect, useMemo } from 'react';

import {
    PrimaryButton,
    SectionTitle,
    SplitContainer,
    VerticalContainer
} from '@american-autonomy/rantizo-ui';

import ItemCard from 'components/cards/ItemCard';

import useCurrentOrganization from 'hooks/useCurrentOrganization';

import useTranslation from './hooks/useTranslation';

import { LEARN_MORE_URL, TEST_ID, UPGRADE_URL } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const OrganizationSubscriptionDetails: FunctionComponent<Props> = ({ testId = TEST_ID }) => {
    const {
        ESSENTIALS_SUITE,
        FREE_ACCOUNT,
        LEARN_BUTTON,
        PREMIER_SUITE,
        SUBSCRIPTION,
        TRIAL,
        UPGRADE_BUTTON
    } = useTranslation();

    const { currentOrganization, refreshCurrentOrganization } = useCurrentOrganization();

    // Persisted org goes stale on out-of-session changes (e.g. the Stripe webhook
    // upgrading the tier right after checkout). Refresh on mount; keep the cached
    // copy on transient failure.
    useEffect(() => {
        const abortController = new AbortController();

        void refreshCurrentOrganization(abortController.signal);

        return () => abortController.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const subscription = currentOrganization?.subscription ?? 'FREE';

    const subscriptionTextMap: { [key: string]: string } = useMemo(
        () => ({
            ESSENTIALS: ESSENTIALS_SUITE,
            FREE: FREE_ACCOUNT,
            PREMIER: PREMIER_SUITE,
            TRIAL: TRIAL
        }),
        [TRIAL, FREE_ACCOUNT, ESSENTIALS_SUITE, PREMIER_SUITE]
    );

    const openLink = useCallback((link: string) => {
        window.open(link, '_blank');
    }, []);

    return (
        <VerticalContainer
            className={styles.organizationSubscriptionDetailsContainer}
            testId={testId}
        >
            <SectionTitle text={SUBSCRIPTION} />

            <ItemCard>{subscriptionTextMap[subscription] ?? ''}</ItemCard>

            <SplitContainer>
                <PrimaryButton onClick={() => openLink(UPGRADE_URL)} text={UPGRADE_BUTTON} />

                <PrimaryButton onClick={() => openLink(LEARN_MORE_URL)} text={LEARN_BUTTON} />
            </SplitContainer>
        </VerticalContainer>
    );
};

export default OrganizationSubscriptionDetails;
